import { DataListEntryCellValue } from '@/types/data-lists'
import useCreateDetail, {
  UseCreateDetailOptions,
} from '../core-hooks/useCreateDetail'
import useDeleteDetail, {
  UseDeleteDetailOptions,
} from '../core-hooks/useDeleteDetail'
import useDeleteList, {
  UseDeleteListOptions,
} from '../core-hooks/useDeleteList'
import useGetDetail, { UseGetDetailOptions } from '../core-hooks/useGetDetail'
import useGetList, { UseGetListOptions } from '../core-hooks/useGetList'
import useUpdateDetail, {
  UseUpdateDetailOptions,
} from '../core-hooks/useUpdateDetail'
import useUpdateList, {
  UseUpdateListOptions,
} from '../core-hooks/useUpdateList'
import buildService from '../create-service'
import queryKeys from '../query-keys'

const service = buildService<DataListEntryCellValue>({
  pathCategory: 'data_list_entry_cell_values',
})

export function useGetDataListEntryCellValue({
  id,
  ...options
}: UseGetDetailOptions<DataListEntryCellValue>) {
  const query = useGetDetail<DataListEntryCellValue>({
    id,
    queryKey: queryKeys.dataListEntryCellValues.detail({
      id,
      filters: options.filters,
    }),
    serviceFn: service.getDetail,
    ...options,
  })

  return {
    dataListEntryCellValue: query.data,
    ...query,
  }
}

export function useCreateDataListEntryCellValue(
  options: UseCreateDetailOptions<DataListEntryCellValue> = {},
) {
  const mutation = useCreateDetail<DataListEntryCellValue>({
    serviceFn: service.createDetail,
    ...options,
  })

  return {
    createDataListEntryCellValue: mutation.mutate,
    createDataListEntryCellValueAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useUpdateDataListEntryCellValue(
  options: UseUpdateDetailOptions<DataListEntryCellValue> = {},
) {
  const mutation = useUpdateDetail<DataListEntryCellValue>({
    serviceFn: service.updateDetail,
    ...options,
  })

  return {
    updateDataListEntryCellValue: mutation.mutate,
    updateDataListEntryCellValueAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteDataListEntryCellValue(
  options: UseDeleteDetailOptions = {},
) {
  const mutation = useDeleteDetail<DataListEntryCellValue>({
    serviceFn: service.deleteDetail,
    ...options,
  })

  return {
    deleteDataListEntryCellValue: mutation.mutate,
    deleteDataListEntryCellValueAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useGetDataListEntryCellValues({
  filters,
  ...options
}: UseGetListOptions<DataListEntryCellValue> = {}) {
  const query = useGetList<DataListEntryCellValue>({
    filters,
    queryKey: queryKeys.dataListEntryCellValues.list({ filters }),
    serviceFn: service.getList,
    ...options,
  })

  return {
    dataListEntryCellValues: query.allData,
    ...query,
  }
}

export function useUpdateDataListEntryCellValues(
  options: UseUpdateListOptions<DataListEntryCellValue> = {},
) {
  const mutation = useUpdateList<DataListEntryCellValue>({
    serviceFn: service.updateList,
    ...options,
  })

  return {
    updateDataListEntryCellValues: mutation.mutate,
    updateDataListEntryCellValuesAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteDataListEntryCellValues({
  sideEffectQueryKeys,
  listQueryKey,
  ...options
}: UseDeleteListOptions = {}) {
  const mutation = useDeleteList<DataListEntryCellValue>({
    serviceFn: service.deleteList,
    sideEffectQueryKeys,
    listQueryKey,
    ...options,
  })

  return {
    deleteDataListEntryCellValues: mutation.mutate,
    deleteDataListEntryCellValuesAsync: mutation.mutateAsync,
    ...mutation,
  }
}
